import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { UserPreferencesProvider } from './context/UserPreferencesContext';
import { PinVerificationProvider, usePinVerification } from './context/PinVerificationContext';
import { PermissionsProvider, usePermissionsContext } from './context/PermissionsContext';
import PinVerificationModal from './components/Modals/PinVerificationModal';
import useUserRoles from './hooks/Home/useUserRoles';
import { tokenStorage } from './utils/encryptedCookie';
import Login from './components/Login/Login';
import Home from './components/Home/Home';
import Dashboard from './components/Dashboard/Dashboard';
import Profile from './components/Profile/Profile';
import Settings from './components/Settings/Settings';
import Person from './components/Person/Person';
import Farm from './components/Farm/Farm';
import Harvest from './components/Harvest/Harvest';
import Company from './components/Company/Company';
import Users from './components/Users/Users';
import Product from './components/Product/Product';
import Lot from './components/Lot/Lot';
import Silo from './components/Silo/Silo';
import Discounts from './components/Discounts/Discounts';
import Romaneios from './components/Romaneios/Romaneios';
import Transfer from './components/Transfer/Transfer';
import Romaneio from './components/Reports/Romaneio';
import Estoque from './components/Reports/Estoque';
import Saldos from './components/Reports/Saldos';
import Logs from './components/Logs/Logs';
import ProtectedRoute from './components/Routes/ProtectedRoute';
import './styles/App.css';

function AppContent() {
  const navigate = useNavigate();
  const userRoles = useUserRoles();
  const hasAdminRole = userRoles.includes('ADMIN');
  const { permissions, hasPermission } = usePermissionsContext();
  const { showPinModal, showActionPinModal, attempts, actionAttempts, handlePinVerify, handleActionPinVerify, closeActionPinModal, checkPinRequired } = usePinVerification();

  // Helper function: se tiver permissões configuradas, usa elas; senão, usa ADMIN
  const canAccess = (permissionKey) => {
    const hasAnyPermissions = Object.keys(permissions).length > 0;
    if (hasAnyPermissions) {
      return hasPermission(permissionKey);
    }
    return hasAdminRole;
  };

  useEffect(() => {
    const token = tokenStorage.get();
    if (token && (window.location.pathname === '/' || window.location.pathname === '/login' || window.location.pathname === '/dashboard')) {
      // Se tiver permissões configuradas, verifica chartsView; senão, usa ADMIN
      if(canAccess('chartsView')) {
        navigate('/dashboard');
      } else {
        navigate('/romaneios');
      }
    }
  }, [navigate, hasAdminRole, permissions, hasPermission]);

  useEffect(() => {
    const token = tokenStorage.get();
    if (token) {
      checkPinRequired();
    }
  }, [checkPinRequired]);

  return (
    <>
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="profile" element={<Profile />} />
        <Route path="settings" element={<Settings />} />
        <Route path="romaneios" element={<Romaneios />} />
        <Route path="reports/romaneio" element={<Romaneio />} />
        <Route path="reports/estoque" element={<Estoque />} />
        <Route path="reports/saldos" element={<Saldos />} />
        <Route path="person" element={<Person />} />
        <Route path="farm" element={<Farm />} />
        <Route path="harvest" element={<Harvest />} />
        <Route path="product" element={<Product />} />
        <Route path="silo" element={<Silo />} />
        <Route path="lot" element={<Lot />} />
        
        <Route
          path="company"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <Company />
            </ProtectedRoute>
          }
        />
        <Route
          path="discounts"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <Discounts />
            </ProtectedRoute>
          }
        />
        <Route
          path="users"
          element={
              <ProtectedRoute requiredPermission="usersView">
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="transfer"
          element={
              <ProtectedRoute requiredPermission="transferView">
              <Transfer />
            </ProtectedRoute>
          }
        />
          <Route
            path="logs"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Logs />
              </ProtectedRoute>
            }
          />
      </Route>

      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
      
      <PinVerificationModal
        show={showPinModal}
        onVerify={handlePinVerify}
        attempts={attempts}
        maxAttempts={3}
      />
      <PinVerificationModal
        show={showActionPinModal}
        onVerify={handleActionPinVerify}
        attempts={actionAttempts}
        maxAttempts={3}
      />
    </>
  );
}

function App() {
  return <AppContent />;
}

function AppWrapper() {
  return (
    <UserPreferencesProvider>
      <Router>
        <PermissionsProvider>
          <PinVerificationProvider>
        <App />
          </PinVerificationProvider>
        </PermissionsProvider>
      </Router>
    </UserPreferencesProvider>
  );
}

export default AppWrapper;